/**
 * Conversation Router — turn classification for multi-step & follow-up commands.
 *
 * (See docs/CONVERSATIONAL-AND-COMPOUND-ROADMAP.md, Phase 0/1.)
 *
 * EarthVoice's intent parser was built for single, self-contained utterances.
 * This module adds the front-door classifier that lets the parser recognize:
 *
 *   - RESET      — "start over", "clear the map" → begin a fresh chain.
 *   - COMPOUND   — one utterance, several ordered actions → split into clauses.
 *   - FOLLOW-UP  — a turn whose meaning depends on the previous turn
 *                  ("now just Africa", "color it by income", "more than that").
 *   - FRESH      — an ordinary self-contained command (the existing path).
 *
 * Why this exists separately from the LLM parser: the *resolution* of a
 * follow-up ("what does 'it' refer to?") is the worker LLM's job — it already
 * receives the globe context (see src/state/globe-context.js). But the four
 * single-utterance routing guards on the client (tryFastQuery, the concept
 * registry guard, isEducationalQuestion, and the worker's forcedToolFor) assume
 * a self-contained sentence. A bare follow-up like "now do income" has no
 * analytical keyword and would be mis-handled by those guards. This classifier
 * is the deterministic signal that says "route this to the context-aware worker
 * path, don't let the fast guards touch it."
 *
 * DESIGN BIAS (important):
 *   - FOLLOW-UP detection is HIGH-RECALL. A false positive is cheap: a fresh
 *     query routed through the contextual path still parses correctly (the
 *     worker just doesn't cache it). A false negative is the harm: a real
 *     follow-up handled by a fast guard gets mis-parsed. So we lean toward
 *     catching follow-ups. Critically, we ONLY need to catch follow-ups that
 *     are *ambiguous or empty without context* (those carrying anaphora or
 *     discourse markers). A follow-up like "find the hotspots" routes to the
 *     right tool whether or not it's flagged, so missing it is harmless.
 *   - COMPOUND splitting is HIGH-PRECISION. A false split breaks a simple
 *     command, so we only split on strong ordered-action markers, never on a
 *     bare "and" between nouns ("bread and butter", "black and white",
 *     "compare GDP and life expectancy").
 *
 * All functions are PURE and individually unit-tested. Nothing here mutates
 * state or calls the network. This module is intentionally NOT yet wired into
 * the hot path — wiring happens in a later, separately-verified increment.
 */

// ── Normalization ────────────────────────────────────────────
function norm(text) {
  return String(text == null ? '' : text)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function wordCount(text) {
  const t = norm(text)
  return t ? t.split(' ').length : 0
}

// ── Reset detection (precise) ────────────────────────────────
// Deliberately narrow: only phrases that unambiguously mean "wipe and restart".
// NOTE: a mid-sequence "never mind, show me X" is a correction (a follow-up),
// NOT a reset, so "never mind" is intentionally excluded here.
const RESET_RE =
  /^(?:start over|start fresh|let'?s start (?:over|fresh|again)|reset(?: the map| everything)?|clear (?:the )?(?:map|everything|globe)|new map|begin again|go back to the (?:start|beginning)|wipe (?:it|the map|everything)(?: clean)?)\b/

export function isReset(text) {
  return RESET_RE.test(norm(text))
}

// ── Compound splitting (precise) ─────────────────────────────
// Imperative verbs that signal a fresh action clause after "and".
const ACTION_VERB =
  '(?:colou?r|highlight|show|find|draw|make|zoom|label|put|add|overlay|compare|trace|outline|circle|mark|filter|sort|rank|count|measure|remove|hide|focus)'

// Strong ordered-action separators. We split on these; we do NOT split on a
// bare "and" unless it is immediately followed by an action verb.
const COMPOUND_SPLIT_RE = new RegExp(
  [
    '\\s*;\\s*', // semicolons
    ',?\\s+and then\\s+', // "..., and then ..."
    ',?\\s+then\\s+', // "..., then ..."
    '\\s+and also\\s+', // "... and also ..."
    ',?\\s+after that,?\\s+', // "... after that ..."
    `\\s+and\\s+(?=${ACTION_VERB}\\b)`, // "... and color ..." (verb-led)
  ].join('|'),
  'i'
)

const LEADING_FILLER_RE = /^(?:and |also,? |um,? |uh,? |so,? |ok(?:ay)?,? |well,? )+/i

/**
 * Split a compound utterance into ordered clauses. Returns an array of trimmed
 * clause strings (length 1 if the utterance is not compound). Caps at 5 parts.
 */
export function splitCompound(text) {
  const t = norm(text)
  if (!t) return ['']
  const rawParts = t.split(COMPOUND_SPLIT_RE)
  const parts = rawParts
    .map((p) => (p || '').replace(LEADING_FILLER_RE, '').trim())
    .filter((p) => p.length > 0)
  if (parts.length <= 1) return [t]
  return parts.slice(0, 5)
}

export function isCompound(text) {
  return splitCompound(text).length > 1
}

// ── Follow-up detection (high recall) ────────────────────────
// 1) Leading discourse markers that signal continuation of the prior turn.
const FOLLOWUP_LEAD_RE =
  /^(?:now\b|now,|ok(?:ay)?,? now|just\b|and just\b|only\b|also\b|also,|what about\b|how about\b|instead\b|actually\b|wait\b|no,|make it\b|make them\b|colou?r it\b|colou?r them\b|highlight (?:it|them|those|that)\b|zoom\b|focus on\b|add\b|overlay\b|hide\b|remove\b|go back\b)/

// 2) Bare anaphora — pronouns that can only resolve against prior context.
const ANAPHORA_RE = /\b(?:it|that|those|them|they|these|this|there|then)\b/

// 3) Comparison or sameness that points back at the previous result.
const BACKREF_RE =
  /\b(?:more|less|bigger|smaller|larger|higher|lower|fewer|greater)\b.*\bthan that\b|\bthe same\b|\bcompare (?:it|them|that)\b|\bthan (?:that|those)\b/

/**
 * Is this utterance a context-dependent follow-up? High recall by design.
 * Compound utterances are classified by their FIRST clause for this purpose,
 * but callers should normally test isReset → isCompound → isFollowUp in order.
 */
export function isFollowUp(text) {
  const t = norm(text)
  if (!t) return false
  if (FOLLOWUP_LEAD_RE.test(t)) return true
  if (BACKREF_RE.test(t)) return true
  // Short utterance carrying a bare pronoun and no obvious fresh subject.
  // Short = the follow-up is leaning on context rather than naming a new target.
  if (ANAPHORA_RE.test(t) && wordCount(t) <= 7) return true
  return false
}

// ── Unified classifier ───────────────────────────────────────
/**
 * Classify a turn. Returns:
 *   { kind: 'reset' | 'compound' | 'followup' | 'fresh', parts: string[] }
 * Precedence: reset → compound → followup → fresh.
 *   - parts is the clause list for 'compound', else [normalizedText].
 */
export function classifyTurn(text) {
  const t = norm(text)
  if (isReset(t)) return { kind: 'reset', parts: [t] }
  const parts = splitCompound(t)
  if (parts.length > 1) return { kind: 'compound', parts }
  if (isFollowUp(t)) return { kind: 'followup', parts: [t] }
  return { kind: 'fresh', parts: [t] }
}

const conversationRouter = {
  isReset,
  isCompound,
  splitCompound,
  isFollowUp,
  classifyTurn,
}

export default conversationRouter
